import React, { useState } from "react";
import { Button, TextInput, Textarea } from "@mantine/core";
import apiService from "../../services/api";
import type { PetSetupProps } from "../../types";

interface PetFormEntry {
  name: string;
  age: string;
  breed: string;
  target_behaviors: string;
}

const PetSetup: React.FC<PetSetupProps> = ({ onComplete, onBackToHome, clientId }) => {
  const [pets, setPets] = useState<PetFormEntry[]>([
    { name: "", age: "", breed: "", target_behaviors: "" },
  ]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handlePetChange = (index: number, field: keyof PetFormEntry, value: string) => {
    const updatedPets = [...pets];
    updatedPets[index][field] = value;
    setPets(updatedPets);
  };

  const addPet = () => {
    setPets([...pets, { name: "", age: "", breed: "", target_behaviors: "" }]);
  };

  const removePet = (index: number) => {
    if (pets.length > 1) {
      const updatedPets = pets.filter((_, i) => i !== index);
      setPets(updatedPets);
    }
  };

  const validatePets = (): boolean => {
    for (let i = 0; i < pets.length; i++) {
      const pet = pets[i];
      if (!pet.name.trim()) {
        setError(`Pet ${i + 1}: Name is required`);
        return false;
      }
      if (!pet.age || Number(pet.age) < 0 || Number(pet.age) > 30) {
        setError(`Pet ${i + 1}: Please enter a valid age (0-30 years)`);
        return false;
      }
      if (!pet.breed.trim()) {
        setError(`Pet ${i + 1}: Breed is required`);
        return false;
      }
      if (!pet.target_behaviors.trim()) {
        setError(`Pet ${i + 1}: Target behaviors are required`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validatePets()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Save each pet
      for (const pet of pets) {
        await apiService.createPet({
          client_id: clientId,
          pet_name: pet.name,
          pet_age: parseInt(pet.age),
          pet_breed: pet.breed,
          target_behaviors: pet.target_behaviors,
        });
      }

      // Redirect to client portal
      onComplete();
    } catch (err: unknown) {
      console.error("Pet setup error:", err);
      setError((err as Error).message || "Failed to save pet information");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-sans bg-background min-h-screen flex items-center justify-center p-5">
      <div className="bg-card rounded-2xl p-5 sm:p-8 md:p-10 shadow-[0_20px_60px_rgba(230,126,34,0.15)] border border-border max-w-[600px] w-full max-h-[90vh] overflow-y-auto">
        <button
          onClick={onBackToHome}
          className="bg-transparent border-none text-muted-foreground hover:text-foreground text-base cursor-pointer mb-5 flex items-center gap-2 transition-colors"
        >
          ← Back to Home
        </button>

        <div className="text-center mb-8">
          <div className="w-[60px] h-[60px] bg-gradient-to-r from-primary to-primary-hover rounded-xl flex items-center justify-center mx-auto mb-4">
            <img src="/images/logo.png" alt="Pheefee Logo" className="w-10 h-10 object-contain" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            Tell Us About Your Pets
          </h1>
          <p className="text-muted-foreground text-base leading-relaxed">
            Add information about your dogs so trainers can provide the best care
          </p>
        </div>

        {error && (
          <div className="text-destructive text-sm mb-5 p-3 bg-destructive/10 border border-destructive/25 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {pets.map((pet, index) => (
            <div key={index} className="mb-8 p-5 border-2 border-border rounded-xl bg-muted">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-lg font-semibold text-foreground">🐕 Pet {index + 1}</h3>
                {pets.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePet(index)}
                    className="bg-destructive text-white border-none rounded-full w-[30px] h-[30px] cursor-pointer text-xl flex items-center justify-center hover:opacity-90 transition-opacity"
                  >
                    ×
                  </button>
                )}
              </div>

              <TextInput
                label="Pet Name *"
                value={pet.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handlePetChange(index, "name", e.target.value)
                }
                placeholder="e.g., Buddy, Luna, Max"
                required
                radius="md"
                size="md"
                className="mb-5"
              />

              <TextInput
                label="Age (years) *"
                type="number"
                value={pet.age}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handlePetChange(index, "age", e.target.value)
                }
                placeholder="e.g., 3"
                min={0}
                max={30}
                required
                radius="md"
                size="md"
                className="mb-5"
              />

              <TextInput
                label="Breed *"
                value={pet.breed}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handlePetChange(index, "breed", e.target.value)
                }
                placeholder="e.g., Golden Retriever, Mixed, German Shepherd"
                required
                radius="md"
                size="md"
                className="mb-5"
              />

              <Textarea
                label="Target Behaviors *"
                value={pet.target_behaviors}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handlePetChange(index, "target_behaviors", e.target.value)
                }
                placeholder="e.g., Basic obedience, house training, socialization, leash walking, etc."
                required
                radius="md"
                size="md"
                minRows={3}
              />
            </div>
          ))}

          <button
            type="button"
            onClick={addPet}
            className="bg-transparent border-2 border-dashed border-primary text-primary p-5 rounded-lg cursor-pointer text-base font-semibold w-full mb-8 flex items-center justify-center gap-2.5 hover:bg-primary-subtle transition-colors"
          >
            + Add Another Pet
          </button>

          <Button type="submit" loading={loading} fullWidth radius="md" size="lg">
            Finish Setup
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PetSetup;
